import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { 
    cartItems = [], 
    packagingItems = [], 
    updateQuantity, 
    removeFromCart, 
    getCartTotal = () => 0, 
    getPackagingTotal = () => 0, 
    getGrandTotal = () => 0, 
    clearCart 
  } = useCart() || {};
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('A kosár üres!');
      return;
    }
    setIsCheckingOut(true);
    navigate('/checkout');
  };

  if (cartItems.length === 0 && !isCheckingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-4">
              A kosarad üres
            </h2>
            <p className="text-gray-600 mb-8">
              Adj hozzá néhány finom ételt az étlapról!
            </p>
            <button
              onClick={() => navigate('/menu')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Vissza az étlaphoz</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/menu')}
            className="inline-flex items-center space-x-2 text-[#00BCD4] hover:text-[#00ACC1] font-semibold mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Vissza az étlaphoz</span>
          </button>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900">
            Kosarad
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {item.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {item.category}
                    </p>
                    
                    {/* Show customer note if exists */}
                    {item.customerNote && (
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-2 mb-3">
                        <p className="text-sm text-orange-700 font-semibold">
                          💬 Megjegyzés: {item.customerNote}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xl font-black text-[#FFA726]">
                      {item.price} Ft
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex flex-col items-end space-y-3 ml-4">
                    <button
                      onClick={() => removeFromCart(item.cartItemId)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    <div className="flex items-center space-x-3 bg-gray-100 rounded-xl p-2">
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                        className="p-1 hover:bg-white rounded-lg transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-bold text-gray-900 w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                        className="p-1 hover:bg-white rounded-lg transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">
                      Összesen: <span className="font-bold">{item.price * item.quantity} Ft</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={clearCart}
              className="w-full py-3 text-red-500 hover:bg-red-50 rounded-xl font-semibold transition-colors"
            >
              Kosár ürítése
            </button>

            {/* Packaging Section */}
            {packagingItems.length > 0 && (
              <div className="mt-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Csomagolás (automatikusan hozzáadva)
                  </h3>
                  <div className="space-y-3">
                    {packagingItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between bg-white rounded-xl p-4"
                      >
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600">
                            {item.quantity}x {item.price} Ft
                          </p>
                        </div>
                        <p className="font-bold text-blue-600">
                          {item.price * item.quantity} Ft
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700 mt-3">
                    💡 A csomagolási költség automatikusan kerül hozzáadásra az ételek alapján
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Rendelés összesítő
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Tételek száma:</span>
                  <span className="font-semibold">
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)} db
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Ételek összesen:</span>
                  <span className="font-semibold">{getCartTotal()} Ft</span>
                </div>
                {packagingItems.length > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Csomagolás:</span>
                    <span className="font-semibold text-blue-600">{getPackagingTotal()} Ft</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Végösszeg:</span>
                    <span className="text-2xl font-black text-[#FFA726]">
                      {getGrandTotal()} Ft
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Pénztár
              </button>

              <button
                onClick={() => navigate('/menu')}
                className="w-full mt-3 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                További ételek hozzáadása
              </button>

              <div className="mt-6 p-4 bg-gradient-to-br from-[#00BCD4]/10 to-[#FFA726]/10 rounded-xl">
                <p className="text-sm text-gray-600 text-center">
                  <strong>Szállítási lehetőségek:</strong>
                  <br />
                  Elvitel • Házhozszállítás • Helyben fogyasztás
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
